/**
 * Archived-facility ordering — OR-1431.
 *
 * Design spec: archived facilities render at the BOTTOM of every table,
 * "ordered based on the sequence in which they were archived, with the most
 * recently archived appearing last".
 *
 * ⚠️ That sub-ordering is only fully achievable within a session. The backend
 * has no `excluded_at` — `is_excluded` is a bare boolean — and `updated_at`
 * cannot stand in for it: `SubmissionLocation.updated_at` is declared
 * `server_default=func.now()` with **no `onupdate`**
 * (orion-backend `src/models/submission.py`), so it is written once at INSERT
 * and never bumped by a PATCH. Verified live against the dev API: archiving a
 * location returned the exact same `updated_at` it had before. For a location
 * that column is effectively the creation timestamp.
 *
 * So ordering works in two tiers:
 *
 *   1. **Archived this session** — `noteArchiveTransition` stamps a monotonic
 *      counter, giving the exact click order the design asks for. These sort
 *      after every already-archived row (they are the most recent).
 *   2. **Already archived on load** — no archive timestamp exists anywhere, so
 *      these fall back to `updatedAt` (i.e. creation order) purely to keep the
 *      order stable and deterministic across renders. It is NOT the archive
 *      sequence, and can't be until the backend grows an `excluded_at`.
 *
 * The part that always holds — archived rows at the bottom, read-only, struck
 * through — is the visible requirement; only the relative order *among*
 * several rows archived in earlier sessions is approximate. With a single
 * archived facility (the ~95% case per Linda) the two are indistinguishable.
 *
 * Restore is the mirror image: the spec puts a restored facility "at the bottom
 * of the table, as if it had been added last", so a restore also stamps the
 * counter and sorts the row to the end of the ACTIVE group (still above
 * anything archived). That too is a session-lifetime signal — after a reload a
 * restored facility is just an ordinary facility again, in its natural order.
 *
 * Ordering is applied at RENDER time, never to the store's array: the
 * `useFacilities` baseline diff compares with `JSON.stringify`, so reordering
 * the source of truth would mark the page dirty from nothing but a sort.
 */
import type { Facility } from "../types/facility";

/** Row id → session archive sequence. Row ids are UUIDs (or `local-` temp
 *  ids), so no submission scoping is needed. Module-level on purpose: the
 *  facilities store is itself a module-level pubsub. */
const archiveSeqById = new Map<string, number>();
/** Row id → session restore sequence. Restoring puts the facility "at the
 *  bottom of the table, as if it had been added last" (design spec), so a
 *  just-restored row needs to sort after the other active rows. */
const restoreSeqById = new Map<string, number>();
let nextSeq = 1;

/**
 * Record an archive/restore transition so the staged (unsaved) ordering
 * matches the design. Called from `useFacilities.update()`, so every caller
 * — any page's ⋮ menu — gets the ordering for free.
 *
 * Both directions stamp a fresh sequence from the same counter, and each
 * clears the other: a row is either most-recently-archived or
 * most-recently-restored, never both.
 */
export function noteArchiveTransition(id: string, isExcluded: boolean): void {
    if (isExcluded) {
        restoreSeqById.delete(id);
        archiveSeqById.set(id, nextSeq++);
    } else {
        archiveSeqById.delete(id);
        restoreSeqById.set(id, nextSeq++);
    }
}

/** Test seam — resets the session counters between specs. */
export function resetArchiveSeqForTests(): void {
    archiveSeqById.clear();
    restoreSeqById.clear();
    nextSeq = 1;
}

interface Entry<T> {
    item: T;
    index: number;
    facility: Facility;
}

/** Archived-vs-archived ordering: session-stamped rows are the most recent
 *  (they were archived after anything that arrived already archived).
 *  Otherwise fall back to `updatedAt` — creation order, not archive order, but
 *  stable; see the tier-2 note in the module header. ISO-8601 strings compare
 *  correctly lexicographically. */
function compareArchived<T>(a: Entry<T>, b: Entry<T>): number {
    const seqA = archiveSeqById.get(a.facility.id);
    const seqB = archiveSeqById.get(b.facility.id);
    if (seqA !== undefined && seqB !== undefined) return seqA - seqB;
    if (seqA !== undefined) return 1; // a archived this session → later
    if (seqB !== undefined) return -1;
    const byUpdatedAt = a.facility.updatedAt.localeCompare(b.facility.updatedAt);
    return byUpdatedAt !== 0 ? byUpdatedAt : a.index - b.index;
}

/** Active-vs-active ordering: a facility restored in this session goes to the
 *  bottom of the active rows, "as if it had been added last" (design spec).
 *  Everything else holds its position in the store. */
function compareActive<T>(a: Entry<T>, b: Entry<T>): number {
    const seqA = restoreSeqById.get(a.facility.id);
    const seqB = restoreSeqById.get(b.facility.id);
    if (seqA !== undefined && seqB !== undefined) return seqA - seqB;
    if (seqA !== undefined) return 1;
    if (seqB !== undefined) return -1;
    return a.index - b.index;
}

/**
 * Archived facilities last, in archive order; everything else keeps its
 * existing position.
 *
 * Generic over the row shape so callers can sort a facility paired with its
 * derived pricing — `useFacilitiesCalc` returns an array index-aligned to its
 * input, so the pair has to travel through the sort together rather than the
 * facility array being sorted on its own.
 */
export function sortArchivedLast<T>(items: T[], facilityOf: (item: T) => Facility): T[] {
    return items
        .map((item, index) => ({ item, index, facility: facilityOf(item) }))
        .sort((a, b) => {
            if (a.facility.isExcluded !== b.facility.isExcluded) {
                return a.facility.isExcluded ? 1 : -1;
            }
            if (a.facility.isExcluded) return compareArchived(a, b);
            return compareActive(a, b);
        })
        .map((entry) => entry.item);
}

/** Convenience for the common case of sorting a plain `Facility[]`. */
export function sortFacilitiesArchivedLast(facilities: Facility[]): Facility[] {
    return sortArchivedLast(facilities, (f) => f);
}

/** Non-archived rows — the set every pricing/aggregate sum must run over. */
export function activeFacilities(facilities: Facility[]): Facility[] {
    return facilities.filter((f) => !f.isExcluded);
}

/** Trimmed + lower-cased, for the name fallback in `findFacilityForRow`. */
function norm(value: string | null | undefined): string {
    return (value ?? "").trim().toLowerCase();
}

/**
 * The submission location behind a row keyed on the CATALOG facility.
 *
 * Regulatory's Acuity/Survey rows and Operational's staffing rows carry a
 * global `facility.id` plus a display name — never the `SubmissionLocation.id`
 * — so anything acting on the underlying location has to resolve it first.
 * Matches on the catalog link, then falls back to the name: a facility added
 * from the Schedule tab persists no catalog link, so `facility_id` comes back
 * null and the id match alone would miss it.
 *
 * Lives here rather than in `pages/submission/regulatory/facility-dedup.ts`,
 * where it started, because Operational needs the same join for its ⋮ menu.
 * That module re-exports it.
 */
export function findFacilityForRow(
    facilities: Facility[],
    row: { facilityId: string; facilityName: string },
): Facility | undefined {
    return (
        facilities.find((f) => row.facilityId !== "" && f.catalogFacilityId === row.facilityId) ??
        facilities.find((f) => norm(f.name) !== "" && norm(f.name) === norm(row.facilityName))
    );
}

/**
 * The submission location behind a row keyed on a LOCATION REFERENCE — Loss
 * Runs claims, today.
 *
 * Resolves by id; only when the row has no `locationId` does it fall back to an
 * exact trimmed name match, which is the same resolution the Loss Runs Location
 * cell's `selectedKey` uses and covers rows staged before `locationId` existed.
 * A row whose `locationId` points nowhere resolves to nothing rather than
 * silently landing on a same-named facility — duplicate facility names are a
 * real case here (OR-1409/1417).
 */
export function findFacilityForLocationRef(
    facilities: Facility[],
    ref: { locationId: string; location: string },
): Facility | undefined {
    if (ref.locationId) return facilities.find((f) => f.id === ref.locationId);
    const name = ref.location.trim();
    return facilities.find((f) => f.name.trim() === name);
}

/**
 * Does a row that points at a location belong to an archived facility?
 *
 * Shared so the struck rows and the excluded-from-totals rows can't drift
 * apart: the summary cards and the table footer both have to agree with each
 * other AND with the backend's `/loss-runs/summary`, which already drops
 * archived locations.
 */
export function isArchivedLocationRef(
    facilities: Facility[],
    ref: { locationId: string; location: string },
): boolean {
    return findFacilityForLocationRef(facilities, ref)?.isExcluded === true;
}
